'use client';

import { Badge } from '../../components/badge';
import { BodyDefault, BodyStrong } from '../../components/typography';
import { Icon } from '../../components/icon';
import { HStack, VStack, Box } from '@terra/ui-styled-system';
import type { InternalEnrichedFinding } from '../../types';
import { useTranslations } from 'next-intl';
import { Globe02Icon, FlashIcon } from '@terra/ui-icons';
import { isArchivedLifecycle } from '@terra/shared-dto-types';
import { ArchivedLifecycleBadge } from '../../components/archived-lifecycle-badge';

interface FindingAssetsDisplayProps {
  assets: InternalEnrichedFinding['assets'];
}

export function FindingAssetsDisplay({ assets }: FindingAssetsDisplayProps) {
  const t = useTranslations(
    'designSystem.findings.findingDetails.assetsDisplay',
  );
  const endpoints = assets.filter((asset) => asset.type === 'ENDPOINT');
  const webpages = assets.filter((asset) => asset.type === 'WEBPAGE');
  const discoveredAssets = assets.filter(
    (asset) => asset.type === 'DISCOVERED_ASSET',
  );

  return (
    <VStack gap={3} alignItems="stretch">
      {endpoints.map((asset) => (
        <Box
          key={asset.id}
          p={4}
          borderWidth="1px"
          borderColor="border.default"
          borderRadius="lg"
          bg="bg.canvas"
          _hover={{
            borderColor: 'colorPalette.emphasized',
            shadow: 'sm',
            bg: 'bg.subtle',
          }}
          transition="all 0.2s ease-in-out"
          cursor="default"
        >
          <HStack gap={3} alignItems="start">
            <Icon size="sm" color="colorPalette.fg" mt={0.5}>
              <FlashIcon />
            </Icon>
            <VStack gap={1} alignItems="start" flex={1} minWidth={0}>
              <HStack gap={2} alignItems="center">
                {asset.method && (
                  <Badge size="sm" variant="fill">
                    {asset.method}
                  </Badge>
                )}
                <Badge size="sm" variant="emphasis" textTransform="none">
                  {t('endpoint')}
                </Badge>
                {asset.graphqlOperationName && (
                  <Badge size="sm" variant="emphasis" textTransform="none">
                    {t('graphql')}
                  </Badge>
                )}
                {asset.soapOperationName && (
                  <Badge size="sm" variant="emphasis" textTransform="none">
                    {t('soap')}
                  </Badge>
                )}
                <ArchivedLifecycleBadge lifecycle={asset.lifecycle} />
              </HStack>
              <BodyStrong
                truncate
                w="full"
                title={
                  asset.soapOperationName || asset.graphqlOperationName
                    ? `${asset.url || asset.path}?${asset.soapOperationName || asset.graphqlOperationName}`
                    : asset.url || asset.path || undefined
                }
              >
                {asset.soapOperationName || asset.graphqlOperationName
                  ? `${asset.url || asset.path}?${asset.soapOperationName || asset.graphqlOperationName}`
                  : asset.url || asset.path || asset.id}
              </BodyStrong>
            </VStack>
          </HStack>
        </Box>
      ))}

      {webpages.length > 0 && (
        <Box
          p={4}
          borderWidth="1px"
          borderColor="border.default"
          borderRadius="lg"
          bg="bg.canvas"
          _hover={{
            borderColor: 'colorPalette.emphasized',
            shadow: 'sm',
            bg: 'bg.subtle',
          }}
          transition="all 0.2s ease-in-out"
          cursor="default"
        >
          <HStack gap={3} alignItems="start">
            <Icon size="sm" color="colorPalette.fg" mt={0.5}>
              <Globe02Icon />
            </Icon>
            <VStack gap={1} alignItems="start" flex={1} minWidth={0}>
              <Badge size="sm" variant="emphasis" textTransform="none">
                {webpages.length === 1
                  ? t('webpageSingular')
                  : t('webpagesPlural')}
              </Badge>
              {webpages.some((asset) =>
                isArchivedLifecycle(asset.lifecycle),
              ) ? (
                <ArchivedLifecycleBadge lifecycle={{ state: 'ARCHIVED' }} />
              ) : null}
              <BodyDefault color="fg.muted">
                {webpages.length === 1
                  ? t('webpageDiscovered')
                  : t('webpagesDiscovered', { count: webpages.length })}
              </BodyDefault>
            </VStack>
          </HStack>
        </Box>
      )}

      {discoveredAssets.map((asset) => (
        <Box
          key={asset.id}
          p={4}
          borderWidth="1px"
          borderColor="border.default"
          borderRadius="lg"
          bg="bg.canvas"
          _hover={{
            borderColor: 'colorPalette.emphasized',
            shadow: 'sm',
            bg: 'bg.subtle',
          }}
          transition="all 0.2s ease-in-out"
          cursor="default"
        >
          <HStack gap={3} alignItems="start">
            <Icon size="sm" color="colorPalette.fg" mt={0.5}>
              <Globe02Icon />
            </Icon>
            <VStack gap={1} alignItems="start" flex={1} minWidth={0}>
              <Badge size="sm" variant="emphasis" textTransform="none">
                {asset.discoveredAssetType ?? asset.type}
              </Badge>
              <ArchivedLifecycleBadge lifecycle={asset.lifecycle} />
              <BodyStrong
                truncate
                w="full"
                title={
                  asset.port && asset.host
                    ? `${asset.host}:${asset.port}`
                    : asset.host || asset.id
                }
              >
                {asset.port && asset.host
                  ? `${asset.host}:${asset.port}`
                  : asset.host || asset.id}
              </BodyStrong>
            </VStack>
          </HStack>
        </Box>
      ))}
    </VStack>
  );
}
